import { MessageSnapshot } from "~models/message"
import {
  tableName as MessageTableName,
  SqliteMessageDataType,
} from "~services/expo-sqlite/message.schema"
import { MessageModal } from "~services/expo-sqlite"

export const limit = 30

const safeParse = (v) => {
  if (v === null || v === undefined) return []
  if (Array.isArray(v)) return v
  if (typeof v === "object") return v
  if (typeof v !== "string") return []
  try {
    return JSON.parse(v)
  } catch {
    return []
  }
}
/**
 * 将mobx资料型态转为sqlite资料型态
 * 将服务器id保存至sid栏位, 资料新增到本地库会后, 会产生本地库id, 将其保存至id栏位, 作为增删的key
 * 将 MobX 模型中的 id（服务器 ID）存储到 sid
 * 将 MobX 的 sid（SQLite 本地 ID）存储到 id
 * 交换 id/sid，使 id 成为 SQLite 主键
 * @param {MessageSnapshot} cM
 * @return {SqliteMessageDataType}
 */
const messageAdaptorIn = (cM: MessageSnapshot): SqliteMessageDataType => {
  const newV = {
    ...cM,
    sid: cM.id,
    id: cM.sid,
  } as SqliteMessageDataType
  return newV
}

/**
 * 将sqlite资料型态转为mobx资料型态
 * 把 SQLite 主键 id 还原到前端 sid
 * 把服务器 ID sid 还原成前端 id
 * JSON 字段转换回原始结构（attach, mention_ids, read_ids）
 * SQLite 中 int → boolean 转换
 * @param cM 
 * @returns 
 */
const toDate = (value: unknown): Date => {
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value
  const n = Number(value)
  if (Number.isFinite(n) && n > 0) return new Date(n)
  return new Date()
}

const toOptionalNumber = (value: unknown): number | null => {
  if (value == null || value === "") return null
  if (typeof value === "number" && Number.isFinite(value)) return value
  if (typeof value === "string" && /^\d+$/.test(value)) return Number(value)
  return null
}

const messageAdaptorOut = (cM: SqliteMessageDataType): MessageSnapshot => {
  return {
    ...cM,
    sid: toOptionalNumber(cM.id) ?? undefined,
    id: toOptionalNumber(cM.sid),
    sender_id: cM.sender_id,
    receiver_id: cM.receiver_id,
    sender: cM.sender_id,
    receiver: cM.receiver_id,
    created: toDate(cM.created),
    updated: toDate(cM.updated),
    attach: safeParse(cM.attach),
    mention_ids: safeParse(cM.mention_ids),
    read_ids: safeParse(cM.read_ids),
    is_deleted: Boolean(cM.is_deleted),
  }
}

/**
 * 清空整张消息表
 */
export const dropMessages = () => {
  return MessageModal.destroyAll()
}

/**
 * 新增消息
 * MessageSnapshot → SQLite 格式
 * 写入 messages 表
 * 返回插入后的 SQLite Row（含 id）
 * @param cM
 */
export const insertMessage = async ({ cM = {} }: { cM: MessageSnapshot }) => {
  return MessageModal.insert(messageAdaptorIn(cM))
}

/**
 * 根据 message_id 更新消息
 * 用 message_id（服务器生成的消息唯一 ID）查 SQLite
 * 用传入的字段更新快照 mSnapshot
 * 再转换为 SQLite 格式写入 update
 * @param cM
 */
export const updateMessageByMessageID = async ({ cM }: { cM: MessageSnapshot }) => {
  if (!cM?.message_id) return null

  try {
    const mSnapshot = await queryMessageByMessageID(cM.message_id)
    if (!mSnapshot) {
      return insertMessage({ cM })
    }

    Object.keys(cM).forEach((key) => {
      mSnapshot[key] = cM[key]
    })

    const sqliteRow = messageAdaptorIn(mSnapshot)
    if (sqliteRow.id == null || sqliteRow.id === undefined) {
      return insertMessage({ cM: mSnapshot })
    }
    return MessageModal.update(sqliteRow)
  } catch {
    return null
  }
}

/**
 * 先查询本地 SQLite 对应消息
 * 取出 SQLite id（主键）
 * 删除该条记录
 * @param cM
 */
export const deleteMessageByMessageID = async ({ cM }: { cM: MessageSnapshot }) => {
  if (cM && cM.message_id) {
    const mSnapshot = await queryMessageByMessageID(cM.message_id)
    if (mSnapshot) {
      return MessageModal.destroy(messageAdaptorIn(mSnapshot).id)
    }
  }
  return null
}

/**
 * 批量删除消息（避免逐条 SELECT + DELETE）
 */
export const deleteMessagesByMessageIDs = async (message_ids: string[]): Promise<boolean> => {
  if (!message_ids.length) return true

  const CHUNK_SIZE = 200
  try {
    for (let i = 0; i < message_ids.length; i += CHUNK_SIZE) {
      const chunk = message_ids.slice(i, i + CHUNK_SIZE)
      const placeholders = chunk.map(() => "?").join(",")
      const sql = `DELETE FROM ${MessageTableName} WHERE message_id IN (${placeholders});`
      await MessageModal.excuteSql(sql, chunk)
    }
    return true
  } catch (error) {
    return false
  }
}

/**
 * 根据 message_id 查找单条消息
 * 通过服务器 message_id 查本地消息
 * @param message_id
 */
export const queryMessageByMessageID = async (message_id): Promise<MessageSnapshot | null> => {
  const sql = `SELECT * FROM ${MessageTableName} WHERE message_id = '${message_id}' ORDER BY created DESC LIMIT 1 OFFSET 0;`
  const rows = await MessageModal.excuteSql(sql, [])
  return rows.map(messageAdaptorOut)[0] || null
}

/**
 * 根据 chat_cid 分页查询消息
 * @param chat_cid
 * @param offset
 */
export const queryMessagesByChatID = async (chat_cid, offset): Promise<MessageSnapshot[]> => {
  return queryMessagesByChatIdentity({ chat_id: String(chat_cid) }, offset)
}

export function collectChatIdentityKeys(chat: {
  chat_id?: string
  id?: number | null
  name?: string | null
}): string[] {
  return [
    ...new Set(
      [chat.chat_id, chat.name, chat.id != null ? String(chat.id) : null].filter(
        Boolean,
      ) as string[],
    ),
  ]
}

/** 群/频道可能以数字 id 或 WS cid 落库，需合并查询 */
export const queryMessagesByChatIdentity = async (
  chat: { chat_id: string; id?: number | null; name?: string | null },
  offset: number,
): Promise<MessageSnapshot[]> => {
  const keys = collectChatIdentityKeys(chat)
  if (!keys.length && chat.id == null) return []
  try {
    const cidClause =
      keys.length > 0 ? `chat_cid IN (${keys.map(() => "?").join(", ")})` : "0"
    const idClause = chat.id != null ? "chat_id = ?" : "0"
    const sql = `SELECT * FROM ${MessageTableName} WHERE (${cidClause} OR ${idClause}) ORDER BY created DESC, id DESC LIMIT ${limit} OFFSET ${offset}`
    const params = [...keys, ...(chat.id != null ? [chat.id] : [])]
    const rows = await MessageModal.excuteSql(sql, params)
    return rows.map(messageAdaptorOut)
  } catch (error) {
    return []
  }
}

/**
 * 根据 chat_cid 删除分页消息
 * @param chat_cid
 */
export const deleteMessagesByChatID = async (chat_cid: string): Promise<boolean> => {
  try {
    const sql = `DELETE FROM ${MessageTableName} WHERE chat_cid = ?;`
    const result = await MessageModal.excuteSql(sql, [chat_cid])

    // 根据你封装的 excuteSql 返回结构判断
    return !!result
  } catch (error) {
    return false
  }
}
